import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';

import {
  CommunicationConfigService,
  CommunicationProviderConfig,
  CommunicationProviderConfigDto,
  MessageChannel,
  MESSAGE_CHANNELS,
} from '../../services/communication-config';
import { DropdownService, DropdownItem } from '../../services/dropdown';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-communication-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    CheckboxModule,
  ],
  templateUrl: './communication-config.html',
  styleUrls: ['./communication-config.css'],
})
export class CommunicationConfigPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(CommunicationConfigService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  readonly channels = MESSAGE_CHANNELS;

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  search = signal('');
  rows = signal<CommunicationProviderConfig[]>([]);
  channelFilter = signal<MessageChannel | null>(null);
  partnerOptions = signal<DropdownItem[]>([]);

  // Secret reveal toggles for the dialog.
  showApiKey = signal(false);
  showApiSecret = signal(false);
  showAuthToken = signal(false);

  /** Set when a WhatsApp row's ExtraConfigJson doesn't parse — the named fields can't
   *  round-trip it, so the dialog falls back to the raw JSON textarea. */
  extraParseFailed = signal(false);

  /** Channels with more than one ACTIVE GLOBAL row. Only one is used at send time and
   *  which one wins is undefined — surface it instead of letting sends behave randomly. */
  dupActiveGlobalChannels = computed(() => {
    const count = new Map<string, number>();
    for (const r of this.rows()) {
      if (r.isActive && r.partnerId == null) count.set(r.channel, (count.get(r.channel) ?? 0) + 1);
    }
    return [...count.entries()].filter(([, n]) => n > 1).map(([c]) => c);
  });

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter(
      (r) =>
        (r.providerName || '').toLowerCase().includes(q) ||
        (r.channel || '').toLowerCase().includes(q) ||
        (r.senderId || '').toLowerCase().includes(q) ||
        (r.fromAddress || '').toLowerCase().includes(q) ||
        this.scopeLabel(r).toLowerCase().includes(q),
    );
  });

  form: FormGroup = this.fb.group({
    id: [0],
    partnerId: [null],
    channel: [null, Validators.required],
    providerName: ['', Validators.required],
    baseUrl: [null],
    apiKey: [null],
    apiSecret: [null],
    authToken: [null],
    senderId: [null],
    fromAddress: [null],
    extraConfigJson: [null],
    // WHATSAPP named extras — round-tripped through extraConfigJson so the admin never
    // types JSON (other keys in it, e.g. TemplateName, are preserved untouched).
    waAuthHeader: [null],
    waTemplateLang: [null],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.dropdown.partners().subscribe((o) => this.partnerOptions.set(o));
    this.load();
  }

  // ===================== list =====================
  load() {
    this.loading.set(true);
    this.svc.getAll(this.channelFilter()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  onChannelFilter(value: any) {
    this.channelFilter.set(value === '' || value == null ? null : (value as MessageChannel));
    this.load();
  }

  // ===================== display helpers =====================
  /** "Global" for a null partner, else the partner's name (joined or looked-up). */
  scopeLabel(r: CommunicationProviderConfig): string {
    if (r.partnerId == null) return 'Global';
    if (r.partnerName) return r.partnerName;
    const opt = this.partnerOptions().find((o) => +o.value === r.partnerId);
    return opt ? opt.label : `Partner #${r.partnerId}`;
  }

  /** Currently selected channel in the dialog — drives contextual field display. */
  get selectedChannel(): MessageChannel | null {
    return this.form.get('channel')?.value ?? null;
  }

  // ===================== add / edit =====================
  openAdd() {
    this.editing.set(false);
    this.resetSecrets();
    this.extraParseFailed.set(false);
    this.form.reset({
      id: 0,
      partnerId: this.partnerFilterDefault(),
      channel: this.channelFilter() ?? null,
      providerName: '',
      baseUrl: null,
      apiKey: null,
      apiSecret: null,
      authToken: null,
      senderId: null,
      fromAddress: null,
      extraConfigJson: null,
      waAuthHeader: null,
      waTemplateLang: null,
      isActive: true,
    });
    this.showModal.set(true);
  }

  /** New rows default to the current channel filter but no partner pre-pick. */
  private partnerFilterDefault(): number | null {
    return null;
  }

  openEdit(r: CommunicationProviderConfig) {
    this.editing.set(true);
    this.resetSecrets();
    // GetById to pull the full row (secrets may be omitted from the list payload).
    this.svc.getById(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load the config to edit.');
          return;
        }
        const d: CommunicationProviderConfig = res?.data ?? r;
        const extra = this.parseExtra(d.extraConfigJson);
        this.extraParseFailed.set(extra === null && !!(d.extraConfigJson ?? '').toString().trim());
        this.form.reset({
          id: d.id,
          partnerId: d.partnerId ?? null,
          channel: d.channel,
          providerName: d.providerName ?? '',
          baseUrl: d.baseUrl ?? null,
          apiKey: d.apiKey ?? null,
          apiSecret: d.apiSecret ?? null,
          authToken: d.authToken ?? null,
          senderId: d.senderId ?? null,
          fromAddress: d.fromAddress ?? null,
          extraConfigJson: d.extraConfigJson ?? null,
          waAuthHeader: extra?.['AuthHeader'] ?? null,
          waTemplateLang: extra?.['TemplateLang'] ?? null,
          isActive: d.isActive ?? true,
        });
        this.showModal.set(true);
      },
      error: () => this.toast.error('Could not load the config to edit.'),
    });
  }

  closeModal() {
    this.showModal.set(false);
  }

  private resetSecrets() {
    this.showApiKey.set(false);
    this.showApiSecret.set(false);
    this.showAuthToken.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('channel')?.invalid) this.toast.warning('Channel is required.');
      else if (this.form.get('providerName')?.invalid) this.toast.warning('Provider name is required.');
      else this.toast.warning('Please complete the required fields.');
      return;
    }

    const v = this.form.getRawValue();

    // WHATSAPP edits its extras through named fields; rebuild the JSON here so the admin
    // never types it, preserving any other keys (TemplateName, ...) exactly as loaded. The
    // raw textarea only remains authoritative for the parse-failure fallback and other channels.
    let extra = (v.extraConfigJson ?? '').toString().trim();
    if (v.channel === 'WHATSAPP' && !this.extraParseFailed()) {
      const obj = this.parseExtra(extra) ?? {};
      this.setOrDelete(obj, 'AuthHeader', v.waAuthHeader);
      this.setOrDelete(obj, 'TemplateLang', v.waTemplateLang);
      extra = Object.keys(obj).length ? JSON.stringify(obj) : '';
    } else if (extra && !this.isValidJson(extra)) {
      this.toast.warning('Extra Config must be valid JSON.');
      return;
    }

    const dto: CommunicationProviderConfigDto = {
      id: this.editing() ? Number(v.id) : 0,
      partnerId: v.partnerId ?? null,
      channel: v.channel,
      providerName: (v.providerName || '').trim(),
      baseUrl: this.blankToNull(v.baseUrl),
      apiKey: this.blankToNull(v.apiKey),
      apiSecret: this.blankToNull(v.apiSecret),
      authToken: this.blankToNull(v.authToken),
      senderId: this.blankToNull(v.senderId),
      fromAddress: this.blankToNull(v.fromAddress),
      extraConfigJson: extra || null,
      isActive: !!v.isActive,
    };

    this.saving.set(true);
    this.svc.upsert(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save config.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Config updated' : 'Config created'));
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== inline active toggle =====================
  toggleActive(r: CommunicationProviderConfig) {
    const next = !r.isActive;
    // Optimistic flip; revert if the call fails.
    this.patchRow(r.id, { isActive: next });
    this.svc.setActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<CommunicationProviderConfig>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  // ===================== delete =====================
  async remove(r: CommunicationProviderConfig) {
    const ok = await this.toast.confirm(
      `${r.providerName} (${r.channel}, ${this.scopeLabel(r)}) will be permanently deleted. This cannot be undone.`,
      'Delete provider config?',
      'Delete',
    );
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete.');
          return;
        }
        this.toast.success(res?.message || 'Config deleted');
        this.load();
      },
    });
  }

  /** The stored token outranks the API key at send time — an old Meta bearer token left on a
   *  row that now authenticates by API key would silently send the wrong credential. */
  clearAuthToken() {
    this.form.get('authToken')?.setValue(null);
    this.toast.info('Auth token cleared — it is removed when you Save.');
  }

  // ===================== utils =====================
  /** Parse a JSON object string; null for blank / malformed / non-object. */
  private parseExtra(json: any): Record<string, any> | null {
    const s = (json ?? '').toString().trim();
    if (!s) return null;
    try {
      const o = JSON.parse(s);
      return o && typeof o === 'object' && !Array.isArray(o) ? o : null;
    } catch {
      return null;
    }
  }

  /** Write a named extra, or drop the key entirely when blank (defaults apply server-side). */
  private setOrDelete(obj: Record<string, any>, key: string, value: any) {
    const s = (value ?? '').toString().trim();
    if (s) obj[key] = s;
    else delete obj[key];
  }

  private blankToNull(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }

  private isValidJson(s: string): boolean {
    try {
      JSON.parse(s);
      return true;
    } catch {
      return false;
    }
  }
}
